import { useState, useMemo } from 'react'
import { AlertTriangle, Shield, Zap, Filter, Search, Activity, Server, Map, Cpu, Wifi, Lock, Gauge, Target, TrendingDown, Database, Plus, ChevronDown, Power, Upload, RefreshCw, Play, Pause, Send, Trash2, Edit2, X, Lock as LockIcon, FileX, Ban, BarChart3, Eye, Brain, Sliders } from 'lucide-react'

export default function EDRPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedEndpoint, setSelectedEndpoint] = useState(null)
  const [filterSeverity, setFilterSeverity] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Endpoint data with unique EDR metrics
  const [endpoints, setEndpoints] = useState([
    { id: 'PRD-DB-01', name: 'Production Database Server', os: 'Windows Server 2019', status: 'At Risk', health: 72, memoryThreats: 5, processChain: 'Suspicious', fileIntegrity: 8, vulnCount: 3, lastScan: '5 mins ago', incidents: 2 },
    { id: 'HR-LAPTOP-09', name: 'Finance Dept Laptop', os: 'Windows 10', status: 'Warning', health: 85, memoryThreats: 2, processChain: 'Normal', fileIntegrity: 1, vulnCount: 1, lastScan: '15 mins ago', incidents: 1 },
    { id: 'WEB-SERVER-03', name: 'Web Server Production', os: 'Ubuntu 20.04', status: 'Secure', health: 98, memoryThreats: 0, processChain: 'Normal', fileIntegrity: 0, vulnCount: 0, lastScan: '2 mins ago', incidents: 0 },
    { id: 'DEV-MACHINE-04', name: 'Developer Workstation', os: 'macOS', status: 'Secure', health: 95, memoryThreats: 1, processChain: 'Normal', fileIntegrity: 2, vulnCount: 0, lastScan: '8 mins ago', incidents: 0 },
    { id: 'SALES-LAPTOP-02', name: 'Sales Team Laptop', os: 'Windows 11', status: 'At Risk', health: 68, memoryThreats: 4, processChain: 'Suspicious', fileIntegrity: 6, vulnCount: 2, lastScan: '20 mins ago', incidents: 1 },
  ])

  const [threats, setThreats] = useState([
    { id: 'THR-001', endpoint: 'PRD-DB-01', type: 'Memory Injection', severity: 'Critical', behavior: 'Process Hollowing', file: 'svchost.exe', status: 'Detected', action: 'Quarantine' },
    { id: 'THR-002', endpoint: 'HR-LAPTOP-09', type: 'Unauthorized Execution', severity: 'High', behavior: 'Unsigned Binary', file: 'update.exe', status: 'Pending', action: 'Block' },
    { id: 'THR-003', endpoint: 'PRD-DB-01', type: 'Registry Tampering', severity: 'Critical', behavior: 'Defense Evasion', file: 'N/A', status: 'Detected', action: 'Remediate' },
    { id: 'THR-004', endpoint: 'SALES-LAPTOP-02', type: 'Lateral Movement', severity: 'High', behavior: 'Network Connection', file: 'explorer.exe', status: 'Monitoring', action: 'Isolate' },
  ])

  const filteredThreats = useMemo(() => {
    return threats.filter(t => {
      const matchSeverity = filterSeverity === 'all' || t.severity === filterSeverity
      const matchSearch = searchQuery === '' ||
        t.endpoint.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.file.toLowerCase().includes(searchQuery.toLowerCase())
      return matchSeverity && matchSearch
    })
  }, [threats, filterSeverity, searchQuery])

  const endpointStats = [
    { label: 'Total Endpoints', value: endpoints.length, icon: Server, color: 'text-blue-400' },
    { label: 'At Risk', value: endpoints.filter(e => e.status === 'At Risk').length, icon: AlertTriangle, color: 'text-red-400' },
    { label: 'Memory Threats', value: endpoints.reduce((sum, e) => sum + e.memoryThreats, 0), icon: Cpu, color: 'text-orange-400' },
    { label: 'Vulnerabilities', value: endpoints.reduce((sum, e) => sum + e.vulnCount, 0), icon: Shield, color: 'text-yellow-400' },
  ]

  const responseActions = [
    { id: 1, label: 'Kill Process', icon: FileX, color: 'red', desc: 'Terminate threat process' },
    { id: 2, label: 'Quarantine File', icon: LockIcon, color: 'orange', desc: 'Isolate malicious file' },
    { id: 3, label: 'Block Execution', icon: Ban, color: 'red', desc: 'Prevent app launch' },
    { id: 4, label: 'Isolate Endpoint', icon: Wifi, color: 'orange', desc: 'Network isolation' },
    { id: 5, label: 'Remediate Config', icon: Sliders, color: 'purple', desc: 'Fix system config' },
    { id: 6, label: 'Full Scan', icon: RefreshCw, color: 'cyan', desc: 'Complete endpoint scan' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="space-y-6 p-8">

        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 p-8 text-white shadow-2xl border border-purple-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-4xl font-black">FALCON STRIKE EDR</h1>
                <p className="text-purple-100 text-lg font-semibold mt-1">Advanced Endpoint Protection & Behavioral Analysis</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{endpoints.length}</div>
                <div className="text-sm text-purple-100">Endpoints Monitored</div>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-purple-200/50" />
                <input
                  type="text"
                  placeholder="Search endpoints, files, processes..."
                  className="w-full bg-black/20 backdrop-blur-sm border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white placeholder-purple-200/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                />
              </div>
              <button className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-bold transition-all border border-white/20">
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {endpointStats.map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-5 hover:border-slate-600 transition-all shadow-lg">
                <div className="flex items-start justify-between mb-3">
                  <Icon className={`w-6 h-6 ${stat.color}`} />
                  <span className="text-xs font-bold text-green-400">LIVE</span>
                </div>
                <p className="text-slate-400 text-sm font-semibold">{stat.label}</p>
                <p className="text-white text-3xl font-black mt-2">{stat.value}</p>
              </div>
            )
          })}
        </div>

        {/* Tabs Navigation */}
        <div className="flex gap-2 border-b border-slate-700/50 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'endpoints', label: 'Endpoints', icon: Server },
            { id: 'threats', label: 'Behavioral Threats', icon: Brain },
            { id: 'memory', label: 'Memory Analysis', icon: Cpu },
            { id: 'files', label: 'File Integrity', icon: FileX },
            { id: 'response', label: 'Response', icon: Zap },
          ].map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 font-bold whitespace-nowrap transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Process Behavior Analysis */}
              <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                <h3 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-purple-400" />
                  <span>Process Chain Analysis</span>
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="p-3 bg-slate-700/50 rounded border border-slate-600 hover:border-purple-500/50 transition-all">
                    <p className="text-slate-300 font-mono">explorer.exe → svchost.exe → cmd.exe</p>
                    <p className="text-red-400 text-xs mt-1">⚠️ Suspicious Process Chain Detected</p>
                  </div>
                  <div className="p-3 bg-slate-700/50 rounded border border-slate-600">
                    <p className="text-slate-300 font-mono">winlogon.exe → dwm.exe</p>
                    <p className="text-green-400 text-xs mt-1">✓ Normal Process Chain</p>
                  </div>
                </div>
              </div>

              {/* Memory Threat Detection */}
              <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6">
                <h3 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
                  <Cpu className="w-5 h-5 text-orange-400" />
                  <span>Memory Threat Detection</span>
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded">
                    <span className="text-slate-300">Process Injection Attempts</span>
                    <span className="text-red-400 font-bold">12</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded">
                    <span className="text-slate-300">Code Caves Detected</span>
                    <span className="text-orange-400 font-bold">3</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded">
                    <span className="text-slate-300">Heap Spraying Attempts</span>
                    <span className="text-yellow-400 font-bold">1</span>
                  </div>
                </div>
              </div>
            </div>

            {/* EDR-Specific Capabilities */}
            <div className="bg-slate-800/30 rounded-lg border border-slate-700 p-6">
              <h3 className="text-white font-bold text-lg mb-4">EDR Unique Capabilities</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { name: 'Behavioral Analytics', active: true },
                  { name: 'File Integrity Monitor', active: true },
                  { name: 'Device Control', active: true },
                  { name: 'App Whitelisting', active: false },
                  { name: 'Memory Analysis', active: true },
                  { name: 'Exploit Prevention', active: true },
                  { name: 'Network Isolation', active: true },
                  { name: 'Threat Hunting', active: true },
                ].map((cap, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border text-center text-sm font-bold ${
                    cap.active
                      ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                      : 'bg-slate-700/30 border-slate-600/50 text-slate-400'
                  }`}>
                    {cap.active ? '✓' : '○'} {cap.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Endpoints Tab */}
        {activeTab === 'endpoints' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              {endpoints.map(ep => (
                <div key={ep.id} className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 hover:border-slate-600 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-white font-bold">{ep.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          ep.status === 'At Risk' ? 'bg-red-500/20 text-red-300' :
                          ep.status === 'Warning' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-green-500/20 text-green-300'
                        }`}>{ep.status}</span>
                        <span className="text-slate-400 text-sm">{ep.os}</span>
                      </div>
                      <p className="text-slate-400 text-sm">ID: {ep.id} | Last Scan: {ep.lastScan}</p>
                      <div className="mt-3 grid grid-cols-4 gap-3">
                        <div className="text-center">
                          <p className="text-slate-400 text-xs">Health</p>
                          <p className={`text-lg font-bold ${ep.health > 80 ? 'text-green-400' : ep.health > 60 ? 'text-yellow-400' : 'text-red-400'}`}>{ep.health}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-slate-400 text-xs">Memory Threats</p>
                          <p className="text-lg font-bold text-orange-400">{ep.memoryThreats}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-slate-400 text-xs">Files Modified</p>
                          <p className="text-lg font-bold text-yellow-400">{ep.fileIntegrity}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-slate-400 text-xs">Vulnerabilities</p>
                          <p className="text-lg font-bold text-red-400">{ep.vulnCount}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedEndpoint(ep)}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded font-bold transition-all"
                    >
                      Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Behavioral Threats Tab */}
        {activeTab === 'threats' && (
          <div className="space-y-4">
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                placeholder="Search threats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
              />
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
              >
                <option value="all">All Severity</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
              </select>
            </div>
            <div className="space-y-3">
              {filteredThreats.map(threat => (
                <div key={threat.id} className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          threat.severity === 'Critical' ? 'bg-red-500/20 text-red-300' : 'bg-orange-500/20 text-orange-300'
                        }`}>{threat.severity}</span>
                        <h4 className="text-white font-bold">{threat.type}</h4>
                        <span className="text-slate-400 text-sm">({threat.behavior})</span>
                      </div>
                      <p className="text-slate-400 text-sm">Endpoint: {threat.endpoint} | File: {threat.file}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      threat.status === 'Detected' ? 'bg-red-500/20 text-red-300' :
                      threat.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>{threat.status}</span>
                  </div>
                  <div className="flex gap-2">
                    {['Kill Process', 'Quarantine', 'Isolate', 'Details'].map(action => (
                      <button key={action} className="px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 rounded text-xs font-bold transition-all border border-purple-500/30">
                        {action}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Memory Analysis Tab */}
        {activeTab === 'memory' && (
          <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-6 space-y-4">
            <h3 className="text-white font-bold text-lg">Memory Analysis & Threat Hunting</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { metric: 'Suspicious Process Chains', value: 5, threshold: 0 },
                { metric: 'Process Injection Detected', value: 12, threshold: 0 },
                { metric: 'Unsigned Binaries', value: 8, threshold: 2 },
                { metric: 'Hook Detection', value: 3, threshold: 0 },
                { metric: 'Code Caves Found', value: 6, threshold: 1 },
                { metric: 'API Hooking Attempts', value: 2, threshold: 0 },
              ].map((item, idx) => (
                <div key={idx} className="p-4 bg-slate-700/30 rounded border border-slate-600">
                  <p className="text-slate-400 text-sm mb-2">{item.metric}</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-bold text-white">{item.value}</p>
                    <p className={`text-xs ${item.value > item.threshold ? 'text-red-400' : 'text-green-400'}`}>
                      Threshold: {item.threshold}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Integrity Tab */}
        {activeTab === 'files' && (
          <div className="space-y-4">
            <h3 className="text-white font-bold text-lg">File Integrity Monitoring</h3>
            {[
              { file: 'C:\\Windows\\System32\\kernel32.dll', status: 'Modified', severity: 'Critical', detected: '5 mins ago' },
              { file: 'C:\\Program Files\\app.exe', status: 'Modified', severity: 'High', detected: '12 mins ago' },
              { file: '/etc/passwd', status: 'Modified', severity: 'Critical', detected: '18 mins ago' },
              { file: 'C:\\Users\\Admin\\sensitive.doc', status: 'Access Detected', severity: 'High', detected: '25 mins ago' },
            ].map((item, idx) => (
              <div key={idx} className="bg-slate-800/50 rounded-lg border border-slate-700 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-white font-mono text-sm font-bold">{item.file}</p>
                    <p className="text-slate-400 text-sm mt-1">Detected: {item.detected}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      item.severity === 'Critical' ? 'bg-red-500/20 text-red-300' : 'bg-orange-500/20 text-orange-300'
                    }`}>{item.severity}</span>
                    <button className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-bold transition-all">
                      Restore
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Response Tab */}
        {activeTab === 'response' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {responseActions.map(action => {
              const Icon = action.icon
              return (
                <button
                  key={action.id}
                  className="p-4 rounded-lg border-2 border-slate-700 bg-slate-800/50 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all text-left"
                >
                  <Icon className="w-5 h-5 text-purple-400 mb-2" />
                  <h4 className="font-bold text-white text-sm">{action.label}</h4>
                  <p className="text-slate-400 text-xs mt-1">{action.desc}</p>
                </button>
              )
            })}
          </div>
        )}

      </div>
    </div>
  )
}
